import { Link } from "react-router-dom";
import { ArrowRight, Award, Heart, Users, Sparkles, ShieldCheck, MapPin } from "lucide-react";
import { usePageSeo } from "@/hooks/usePageSeo";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHero from "@/components/PageHero";
import heroImg from "@/assets/hero-about.jpg";
import storyImg from "@/assets/service-tours.jpg";

const values = [
  { icon: Heart, title: "Hospitality first", text: "We treat every guest like family — because that's the Dominican way." },
  { icon: ShieldCheck, title: "Safety & trust", text: "Vetted drivers, insured vehicles, transparent pricing. Always." },
  { icon: Sparkles, title: "Detail-obsessed", text: "Cold water, name signs, child seats — every detail thought through." },
];

const stats = [
  { value: "12k+", label: "Travelers served" },
  { value: "4.9", label: "Average rating" },
  { value: "30+", label: "Destinations covered" },
  { value: "8", label: "Years on the road" },
];

const AboutPage = () => {
  usePageSeo("about");

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <PageHero
        eyebrow="About us"
        title={<>Built by locals, <span className="italic text-gradient-ocean">for travelers.</span></>}
        subtitle="Allen Travels RD started as a single van and a promise: that every guest who steps foot in the Dominican Republic should feel taken care of from minute one."
        image={heroImg}
      />

      {/* STORY */}
      <section className="container mx-auto px-4 py-24 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">Our story</span>
          <h2 className="font-display text-4xl md:text-5xl font-semibold mt-3 leading-tight">
            A small idea, a big island.
          </h2>
          <div className="mt-6 space-y-4 text-foreground/85 leading-relaxed">
            <p>
              We grew up here. We know the back roads to Saona, the best coffee stop on the way to Samaná, and the right palm tree to wait under during a sudden afternoon rain.
            </p>
            <p>
              When we started Allen Travels RD, we wanted to share that kind of intimate, local knowledge — but with the polish and reliability that demanding travelers expect.
            </p>
            <p>
              Today we move thousands of guests across the Dominican Republic every year, and we're still obsessed with the same thing: making your trip effortless from the moment you land.
            </p>
          </div>
        </div>
        <div className="relative">
          <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-elegant">
            <img src={storyImg} alt="Caribbean catamaran experience" loading="lazy" className="w-full h-full object-cover" />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-gradient-card rounded-2xl shadow-elegant p-6 border border-border/60 max-w-xs hidden md:block">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-ocean flex items-center justify-center text-primary-foreground">
                <Award className="w-5 h-5" />
              </div>
              <div>
                <div className="font-display font-semibold text-sm">Certified operator</div>
                <div className="text-xs text-muted-foreground">Ministry of Tourism, DR</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-gradient-soft">
        <div className="container mx-auto px-4 py-24">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">What we stand for</span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold mt-3 leading-tight">Three things, every single trip.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {values.map((v) => (
              <div key={v.title} className="bg-card rounded-3xl p-8 shadow-soft border border-border/60 hover:shadow-elegant transition-smooth text-center">
                <div className="w-14 h-14 rounded-2xl bg-gradient-ocean text-primary-foreground flex items-center justify-center shadow-glow mx-auto">
                  <v.icon className="w-6 h-6" />
                </div>
                <h3 className="font-display text-xl font-semibold mt-5">{v.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="container mx-auto px-4 py-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {stats.map((s) => (
            <div key={s.label}>
              <div className="font-display text-4xl md:text-5xl font-semibold text-gradient-ocean">{s.value}</div>
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mt-2">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container mx-auto px-4 pb-24">
        <div className="relative overflow-hidden rounded-[2rem] bg-gradient-hero p-10 md:p-16 text-white shadow-elegant">
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-primary-glow/20 blur-3xl" />
          <div className="relative grid md:grid-cols-2 gap-6 items-center">
            <div>
              <h2 className="font-display text-4xl md:text-5xl font-semibold leading-tight">Travel with people who care.</h2>
              <p className="mt-4 text-white/85 text-lg max-w-md">Let us handle the logistics. You handle the memories.</p>
            </div>
            <div className="flex md:justify-end gap-3 flex-wrap">
              <Link to="/transfers" className="px-7 py-3.5 rounded-full bg-gradient-ocean text-primary-foreground font-semibold shadow-glow hover:-translate-y-0.5 transition-smooth">
                Book a transfer <ArrowRight className="inline w-4 h-4 ml-1.5" />
              </Link>
              <Link to="/contact" className="px-7 py-3.5 rounded-full border border-white/40 text-white font-semibold hover:bg-white/10 transition-smooth">
                Contact us
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
